import { uniq } from "lodash";
import React from "react";
import styled from "styled-components";

import { DisplayName } from "@examspark/common-app";
import { VSpace } from "@examspark/common-ui";
import {
  CardLink,
  CardSelectable,
  LinkCard,
} from "@examspark/common-ui/dist/components/containers/LinkCard";
import { Maybe, Resource } from "@examspark/types";

import UnitContent from "../common/UnitContent";

import ItemMetadata from "./ItemMetadata";

const Styled = styled.main`
  column-count: 3;
  column-gap: min(5rem, 5vw);
  column-fill: balance;

  // sometimes the vocabulary content contains exotic content that can overflow
  contain: inline-size;

  @media (max-width: 1024px) {
    column-count: 2;
  }
  @media (max-width: 768px) {
    column-count: 1;
  }

  max-width: 100%;
  overflow: auto;

  // ensure we aren't cutting off the box shadow of the LinkCards
  padding: 4px;
`;

const StyledLetterBlock = styled.h2`
  margin-bottom: 1rem;
  font-size: 4rem;
  color: #444;
`;

const StyledVocab = styled.div`
  break-inside: avoid;
  margin-bottom: 1.3rem;
`;
const StyledTerm = styled.h3`
  font-weight: normal;
  font-size: 1.3rem;
`;
const StyledDefinition = styled(UnitContent)`
  color: #333;
  font-size: 1.2rem;

  img {
    vertical-align: middle;
  }
  strong {
    /* font-weight: normal; */
  }
  ul,
  li {
    list-style: none;
    padding: 0;
    margin: 1rem 0;
  }
`;

const StyledItemMetadata = styled.div`
  margin-top: 0.5rem;
`;

const Glossary = ({ data }: { data: Array<Maybe<Resource>> }) => {
  const hasMultipleBanks = uniq(data.map((d) => d?.bankId)).length > 1;

  const dataByLetter = data.reduce(
    (acc, resource) => {
      if (!resource || !resource.name?.length) return acc;
      // Canonicalize to ASCII
      let firstLetter = resource.name[0]
        .toUpperCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
      if (firstLetter.match(/[0-9]/)) {
        firstLetter = "0-9";
      }
      if (!acc[firstLetter]) {
        acc[firstLetter] = [];
      }
      acc[firstLetter].push(resource);
      return acc;
    },
    {} as { [key: string]: Array<Maybe<Resource>> },
  );

  return (
    <Styled>
      {dataByLetter &&
        // weirdness: break-after: avoid does not work very well cross-browser,
        // so instead we box together the StyledLetterBlock and the first
        // StyledVocab.
        Object.entries(dataByLetter).map(([letter, vocabs]) => {
          return (
            <React.Fragment key={letter}>
              {vocabs.map((vocab, index) => {
                if (!vocab) return null;
                delete vocab.updatedAt;

                return (
                  <StyledVocab key={vocab.id}>
                    {index === 0 && (
                      <StyledLetterBlock>{letter}</StyledLetterBlock>
                    )}
                    <LinkCard>
                      <VSpace size="0.5rem">
                        <CardLink to={`/vocabulary/${vocab.id}`}>
                          <StyledTerm>
                            <DisplayName resource={vocab} />
                          </StyledTerm>
                        </CardLink>
                        <CardSelectable>
                          <StyledDefinition content={vocab.description ?? ""} />
                        </CardSelectable>
                        <StyledItemMetadata>
                          <ItemMetadata
                            item={vocab}
                            showBank={hasMultipleBanks}
                          />
                        </StyledItemMetadata>
                      </VSpace>
                    </LinkCard>
                  </StyledVocab>
                );
              })}
            </React.Fragment>
          );
        })}
    </Styled>
  );
};

export default Glossary;
